// src/utils/document-generator.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/* ------------------ Branding por defecto ------------------ */
const DEFAULT_BRANDING = {
  logoUrl:
    'https://qmrmkemkctikfoyxpanz.supabase.co/storage/v1/object/public/personalizacion/Logo%20Calins.png',
  lab: {
    nombre: 'CALINS',
    direccion1: 'Rodríguez Peña 2065',
    direccion2: 'Gral. Gutiérrez – Maipú',
    ciudadProvinciaPais: 'Mendoza – ARGENTINA',
    telefono: 'Cel: +54-9-261-5322685',
    email: 'laboratorio@calins.com.ar',
  },
};

/* ------------------ Utils ------------------ */

// Limpia caracteres que la fuente base de jsPDF no soporta bien
function toPdfText(value) {
  const s = String(value ?? '')
    .replace(/\u00A0|\u202F|\u2007|\u2009/g, ' ') // NBSP & narrow spaces
    .replace(/[ΩΩ]/g, 'Ohm')                     // Omega → Ohm (si querés Ω real, luego embebemos fuente)
    .replace(/µ/g, 'u')                          // micro → u
    .replace(/\u2013|\u2014|\u2212/g, '-');      // guiones unicode → '-'
  return s;
}

// Agrupar por block_id (robusto, coerciona a string/trim)
function groupByBlockId(measurements) {
  const byBlock = {};
  (measurements || []).forEach(m => {
    const key = String(m.block_id ?? '').trim();
    if (!byBlock[key]) byBlock[key] = [];
    byBlock[key].push(m);
  });
  return byBlock;
}

// Paginador
function addPageFooter(doc, page, pages) {
  doc.setFontSize(9);
  const label = `Pag. ${page} de ${pages}`;
  const x = doc.internal.pageSize.getWidth() - 14;
  const y = doc.internal.pageSize.getHeight() - 8;
  doc.text(label, x, y, { align: 'right' });
}

// Descargar imagen y convertir a dataURL para jsPDF
async function fetchImageAsDataURL(url) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Bloque de párrafo ancho fijo
function drawParagraph(doc, text, x, y, maxWidth, lineHeight = 5) {
  const lines = doc.splitTextToSize(toPdfText(text), maxWidth);
  lines.forEach((ln) => {
    doc.text(ln, x, y);
    y += lineHeight;
  });
  return y;
}

/* ------------------ PDF ------------------ */

/**
 * Genera el PDF (async por el logo). Si opts.returnBlob = true, devuelve Blob.
 *
 * @param {Object} calibrationData { client, instrument, calibrationDetails, blocks, measurements, technicalName }
 * @param {Object} opts { returnBlob?: boolean, branding?: { logoUrl?: string, lab?: {...} } }
 * @returns {Promise<Blob|undefined>}
 */
export async function generatePdf(calibrationData, opts = {}) {
  const {
    client,
    instrument,
    calibrationDetails,
    blocks = [],
    measurements = [],
    technicalName
  } = calibrationData;

  const branding = { ...DEFAULT_BRANDING, ...(opts.branding || {}) };

  const clientName =
    client?.name ||
    instrument?.client?.name ||
    '—';

  // Fechas
  const calDate = new Date(calibrationDetails?.date || Date.now());
  const calDateStr = calDate.toLocaleDateString();

  const certNumber =
    calibrationDetails?.certificate_number ||
    calibrationDetails?.certificatenumber ||
    '—';

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  // ---------- Logo + encabezado (ajustado) ----------
  const pageW = doc.internal.pageSize.getWidth();

  // Parámetros de logo (podés ajustar)
  const LOGO_W = 42;
  const LOGO_H = 16;
  const LOGO_TOP = 10;
  const LOGO_RIGHT = 14;

  let usedLogoH = 0;

  if (branding.logoUrl) {
    const dataUrl = await fetchImageAsDataURL(branding.logoUrl);
    if (dataUrl) {
      const x = pageW - LOGO_W - LOGO_RIGHT;
      const y = LOGO_TOP;
      doc.addImage(dataUrl, 'PNG', x, y, LOGO_W, LOGO_H, undefined, 'FAST');
      usedLogoH = LOGO_H;
    }
  }

  // Título alineado
  const TITLE_Y = LOGO_TOP + 6;
  doc.setFontSize(16);
  doc.text('CERTIFICADO DE CALIBRACIÓN / MEDICIÓN', 14, TITLE_Y);

  // Calcula dónde empieza el recuadro de datos para NO chocar con el logo
  const headerTop = Math.max(TITLE_Y + 8, LOGO_TOP + usedLogoH + 6);

  // Caja de cabecera (datos clave)
  doc.setDrawColor(220);
  doc.setLineWidth(0.2);
  doc.roundedRect(12, headerTop, 186, 40, 2, 2);

  doc.setFontSize(11);
  doc.text(`Cliente: ${toPdfText(clientName)}`, 16, headerTop + 10);
  doc.text(`Instrumento (Tag): ${toPdfText(instrument?.tag ?? '—')}`, 16, headerTop + 17);
  doc.text(`Fabricante/Modelo: ${toPdfText(`${instrument?.brand ?? '—'}/${instrument?.model ?? '—'}`)}`, 16, headerTop + 24);
  doc.text(`N° Serie: ${toPdfText(instrument?.serial ?? '—')}`, 16, headerTop + 31);

  const rightX = 130;
  doc.text(`N° Certificado: ${toPdfText(certNumber)}`, rightX, headerTop + 10);
  doc.text(`Fecha de Cal./Med.: ${toPdfText(calDateStr)}`, rightX, headerTop + 17);
  doc.text(`Técnico Responsable: ${toPdfText(technicalName || '—')}`, rightX, headerTop + 24);
  doc.text(`Tipo: ${toPdfText(instrument?.type || '—')}`, rightX, headerTop + 31);

  // Bloque de contacto del laboratorio
  const contactTop = headerTop + 46;
  doc.setFontSize(9.5);
  const lab = branding.lab || {};
  const contactLines = [
    lab.nombre && toPdfText(lab.nombre),
    lab.direccion1 && toPdfText(lab.direccion1),
    lab.direccion2 && toPdfText(lab.direccion2),
    lab.ciudadProvinciaPais && toPdfText(lab.ciudadProvinciaPais),
    lab.telefono && toPdfText(lab.telefono),
    lab.email && toPdfText(lab.email),
  ].filter(Boolean);
  let cy = contactTop;
  contactLines.forEach(line => { doc.text(line, 14, cy); cy += 4.2; });

  // ---------- Texto normativo breve ----------
  let y = Math.max(cy + 4, contactTop + 2);
  doc.setFontSize(10);
  y = drawParagraph(
    doc,
    'Este certificado se expide de acuerdo a lo establecido en la Norma ISO/IEC 17025. ' +
      'Documenta la trazabilidad a patrones nacionales/internacionales (SI). ' +
      'No podrá ser reproducido parcialmente sin autorización escrita del laboratorio.',
    14, y, 182
  );
  y += 2;

  // ---------- Procedimiento ----------
  doc.setFontSize(12);
  doc.text('Procedimiento de Calibración', 14, y);
  y += 6;
  doc.setFontSize(10);
  y = drawParagraph(
    doc,
    calibrationDetails?.method ||
      'Por comparación con instrumento patrón. Generación de señal y medición según programa de puntos establecido.',
    14, y, 182
  );
  y += 2;

  // ---------- Resultados ----------
  doc.setFontSize(12);
  doc.text('Resultados obtenidos durante la calibración', 14, y);
  y += 5;

  const byBlock = groupByBlockId(measurements);

  blocks.forEach((block, idx) => {
    const title = `Resultados: ${block.display_name || block.mode || 'Bloque'}`;
    doc.setFontSize(11);
    doc.text(toPdfText(title), 14, y);
    y += 4;

    const rowsForBlock = byBlock[String(block.id).trim()] || [];
    autoTable(doc, {
      startY: y,
      head: [[ 'Setpoint', 'Lectura', 'Lect. Decrec.', 'Error', 'U (k·u_c)' ]],
      body: rowsForBlock.map(m => {
        const sp = Number(m.setpoint);
        const rd = Number(m.reading);
        const rdDec = m.reading_decreasing != null ? Number(m.reading_decreasing) : null;
        const err = (Number.isFinite(rd) && Number.isFinite(sp)) ? rd - sp : null;
        const u = Number(m.u ?? 0);
        const k = Number(m.coverage_k ?? 2);
        const unit = toPdfText(m.unit || '');

        const fmt = (x, suffix = '') =>
          Number.isFinite(x)
            ? toPdfText(`${x} ${suffix}`.trim())
            : (suffix ? toPdfText(`— ${suffix}`) : '—');

        return [
          fmt(sp, unit),
          fmt(rd, unit),
          Number.isFinite(rdDec) ? fmt(rdDec, unit) : '—',
          Number.isFinite(err) ? toPdfText(`${err.toFixed(4)} ${unit}`.trim()) : fmt(null, unit),
          toPdfText(`${Number.isFinite(u) ? u.toFixed(4) : '—'} (k=${k})`)
        ];
      }),
      theme: 'grid',
      styles: { fontSize: 9 },
      headStyles: { fillColor: [242, 242, 242] },
      margin: { left: 14, right: 14 },
    });

    const prev = doc.lastAutoTable;
    y = (prev?.finalY ?? y) + 8;

    // salto si hace falta
    if (y > 260 && idx !== blocks.length - 1) {
      doc.addPage();
      y = 20;
    }
  });

  // ---------- Segunda parte: patrón, condiciones, observaciones/firmas ----------
  if (y > 240) { doc.addPage(); y = 20; }

  doc.setFontSize(12);
  doc.text('Patrón empleado', 14, y);
  y += 5;
  doc.setFontSize(10);
  y = drawParagraph(
    doc,
    calibrationDetails?.standard_description ||
      'Descripción: Clamp Type Multimeter Calibrator.\nN° Serie: —\nCertificado de calibración N°: —\nFecha de calibración: —',
    14, y, 182
  );
  y += 2;

  doc.setFontSize(12);
  doc.text('Condiciones ambientales', 14, y);
  y += 5;
  doc.setFontSize(10);
  const amb = calibrationDetails || {};
  const ambText =
    `Temperatura: ${amb.ambient_temp ?? '—'} °C   ` +
    `Humedad Relativa: ${amb.ambient_humidity ?? '—'} %HR   ` +
    `Presión: ${amb.ambient_pressure ?? '—'} hPa`;
  y = drawParagraph(doc, ambText, 14, y, 182);
  y += 2;

  doc.setFontSize(12);
  doc.text('Observaciones', 14, y);
  y += 5;
  doc.setFontSize(10);
  y = drawParagraph(
    doc,
    calibrationDetails?.notes ||
      'La incertidumbre expandida se calculó como U = k·u_c con k=2 (~95% confianza). ' +
      'El instrumento funciona correctamente y se encuentra dentro de los parámetros especificados (cuando aplica).',
    14, y, 182
  );
  y += 8;

  // Firmas (líneas simples)
  const pageWidth = doc.internal.pageSize.getWidth();
  const col = pageWidth / 2;
  doc.line(20, y, col - 10, y);
  doc.text('Técnico Responsable', (20 + col - 10) / 2, y + 5, { align: 'center' });
  doc.line(col + 10, y, pageWidth - 20, y);
  doc.text('Aprobación/Cliente', (col + 10 + pageWidth - 20) / 2, y + 5, { align: 'center' });

  // ---------- Pie con paginación ----------
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    addPageFooter(doc, p, pages);
  }

  if (opts.returnBlob) {
    const pdfArray = doc.output('arraybuffer');
    return new Blob([pdfArray], { type: 'application/pdf' });
  }
  doc.save(`certificado-${instrument?.tag || 'instrumento'}.pdf`);
}

/* ------------------ Excel (sin cambios fuertes) ------------------ */
export function generateExcel(calibrationData) {
  const { client, instrument, calibrationDetails, blocks = [], measurements = [] } = calibrationData;

  const clientName = client?.name || instrument?.client?.name || '—';

  const allMeasurements = measurements.map(m => ({
    'Bloque': (blocks.find(b => String(b.id).trim() === String(m.block_id).trim())?.display_name) || '',
    'Setpoint': m.setpoint,
    'Lectura (Creciente)': m.reading,
    'Lectura (Decreciente)': m.reading_decreasing,
    'Unidad': m.unit
  }));
  const ws_med = XLSX.utils.json_to_sheet(allMeasurements);

  const cert = [
    ['Certificado de Calibración / Medición'],
    [],
    ['Cliente:', clientName],
    ['Instrumento (Tag):', instrument?.tag ?? '—'],
    ['Fabricante/Modelo:', `${instrument?.brand ?? '—'}/${instrument?.model ?? '—'}`],
    ['Nº Serie:', instrument?.serial ?? '—'],
    ['Fecha:', new Date(calibrationDetails?.date || Date.now()).toLocaleDateString()],
    ['N° Certificado:', calibrationDetails?.certificate_number || calibrationDetails?.certificatenumber || '—'],
    [],
    ['--- RESULTADOS ---'],
  ];

  const byBlock = groupByBlockId(measurements);
  blocks.forEach(block => {
    cert.push([]);
    cert.push([`Resultados para: ${block.display_name || block.mode || 'Bloque'}`]);
    cert.push(['Setpoint', 'Lectura', 'Lect. Decrec.', 'Error']);
    const rowsForBlock = byBlock[String(block.id).trim()] || [];
    rowsForBlock.forEach(m => {
      const err = (Number(m.reading) - Number(m.setpoint));
      cert.push([m.setpoint, m.reading, m.reading_decreasing ?? '—', Number.isFinite(err) ? err.toFixed(4) : '—']);
    });
  });

  const ws_cert = XLSX.utils.aoa_to_sheet(cert);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws_med, 'Mediciones');
  XLSX.utils.book_append_sheet(wb, ws_cert, 'Certificado');

  XLSX.writeFile(wb, `reporte-${instrument?.tag || 'instrumento'}.xlsx`);
}
